import { Col, Input, Row, Text } from 'components';
import { theme } from 'lib';
import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  fieldKey,
  GOALRUSH_BOUNDS,
  GOALRUSH_DEFENDER_PATTERNS,
  RULE_FIELDS,
  RULE_GROUPS,
  RULE_ROWS
} from '../constants';
import { getLeaf } from '../rulesForm';
import { DurationField, RadioOption, Section } from '../../FanWave/components/ui';

const toDecimal = (raw) => String(raw ?? '').replace(/[^0-9.]/g, '');
const toDigits = (raw) => String(raw ?? '').replace(/[^0-9]/g, '');

// One shell for every number field, top-aligned and the same width, so labels, inputs and help
// text line up across a row (and with the duration field, whose label sits 5px above its input).
const FieldShell = ({ label, children }) => (
  <Col start noFlex style={{ flex: '1 1 220px', minWidth: '200px', maxWidth: '320px' }} gap="5px">
    {label && (
      <Text b2 bold marg="0" style={{ fontSize: '13px' }}>
        {label}
      </Text>
    )}
    {children}
  </Col>
);

// Labelled rows in Others: the label in a fixed column on the left, its options or inputs to the
// right on the same line. `offset` drops the label to line up with what it labels (the radio
// titles, or the input boxes under the field labels).
const INLINE_LABEL_WIDTH = '190px';
const INPUT_LABEL_OFFSET = '33px';

const InlineRow = ({ label, offset = '0', children }) => (
  <Row start noFlex wid="100%" gap="20px" style={{ alignItems: 'flex-start' }}>
    <Text
      b2
      bold
      marg={`${offset} 0 0`}
      style={{ fontSize: '14px', flex: `0 0 ${INLINE_LABEL_WIDTH}` }}
    >
      {label}
    </Text>
    <Col noFlex style={{ flex: '1 1 0', minWidth: 0 }}>
      {children}
    </Col>
  </Row>
);

/**
 * Every GoalRush rule, in three sections (session & targets, points, others); the
 * caller saves them together. Defender gaps and bonus targets are always on, so they are a label
 * beside their own row of fields rather than a toggle; the defender pattern is laid out the same. `hide` leaves fields out (the default session
 * has no length); `sessionNote` renders at the top of Session & targets, `sessionExtra` at its end.
 */
const RulesFields = ({ draft, errors, onChange, hide = [], sessionNote, sessionExtra }) => {
  const { t } = useTranslation();

  const renderField = ({ path, kind, bound }) => {
    const label = t(`admin.goalRush.fields.${fieldKey(path)}`);
    const value = getLeaf(draft, path);
    const help = t(`admin.goalRush.help.${fieldKey(path)}`);
    const range = bound ? t('admin.goalRush.range', GOALRUSH_BOUNDS[bound]) : '';

    if (kind === 'pattern') {
      return (
        <InlineRow key={path} label={label}>
          <Row start gap="18px" style={{ flexWrap: 'wrap' }}>
            {GOALRUSH_DEFENDER_PATTERNS.map((pattern) => (
              <RadioOption
                key={pattern}
                checked={value === pattern}
                onSelect={() => onChange(path, pattern)}
                label={t(`admin.goalRush.patterns.${pattern}.label`)}
                description={t(`admin.goalRush.patterns.${pattern}.description`)}
              />
            ))}
          </Row>
        </InlineRow>
      );
    }
    if (kind === 'duration') {
      return (
        <DurationField
          key={path}
          label={label}
          value={value?.value ?? ''}
          unit={value?.unit ?? 'minutes'}
          error={errors[path]}
          description={`${help} ${t('admin.goalRush.durationRange')}`}
          onChange={(next) => onChange(path, { ...value, value: next })}
          onUnitChange={(unit) => onChange(path, { ...value, unit })}
        />
      );
    }
    const decimal = kind === 'decimal' || kind === 'seconds';
    return (
      <FieldShell key={path} label={label}>
        <Input
          type="text"
          inputMode={decimal ? 'decimal' : 'numeric'}
          value={value}
          error={!!errors[path]}
          onChange={(e) =>
            onChange(path, decimal ? toDecimal(e.target.value) : toDigits(e.target.value))
          }
        />
        <Text
          c1
          col={errors[path] ? theme.SECONDARY : theme.GREY_60}
          marg="0"
          style={{ fontSize: '11px' }}
        >
          {errors[path] || `${help} ${range}`}
        </Text>
      </FieldShell>
    );
  };

  const fieldRow = (fields) => (
    <Row start gap="20px" wid="100%" style={{ flexWrap: 'wrap', alignItems: 'flex-start' }}>
      {fields.map(renderField)}
    </Row>
  );

  return (
    <Col noFlex wid="100%" gap="16px">
      {RULE_GROUPS.map((group) => {
        const fields = RULE_FIELDS.filter((f) => f.group === group && !hide.includes(f.path));
        const loose = fields.filter((f) => !f.row);
        return (
          <Section
            key={group}
            title={t(`admin.goalRush.groups.${group}`)}
            description={t(`admin.goalRush.applies.${group}`)}
          >
            <Col noFlex wid="100%" gap="18px">
              {group === 'session' && sessionNote}
              {loose.length > 0 && fieldRow(loose)}
              {RULE_ROWS.map((row) => {
                const rowFields = fields.filter((f) => f.row === row);
                if (!rowFields.length) return null;
                return (
                  <InlineRow
                    key={row}
                    label={t(`admin.goalRush.rows.${row}`)}
                    offset={INPUT_LABEL_OFFSET}
                  >
                    {fieldRow(rowFields)}
                  </InlineRow>
                );
              })}
              {group === 'session' && sessionExtra}
            </Col>
          </Section>
        );
      })}
    </Col>
  );
};

export default RulesFields;
